import { supabaseClient } from 'supabase'
import { CreateCampaignData, CampaignUpdate } from './types'

export async function updateCampaign(id: string, data: CampaignUpdate) {
  // Validate that campaign exists
  const { data: existingCampaign, error: fetchError } = await supabaseClient
    .from('campaigns')
    .select('*')
    .eq('id', id)
    .single()

  if (fetchError || !existingCampaign) {
    throw new Error('Campaign not found')
  }

  // Check if campaign is active and warn about editing
  if (existingCampaign.status === 'Active' && !data.manually_paused) {
    console.warn('Editing active campaign:', id)
  }

  // Validate date range if dates are being updated
  if (data.start_date || data.end_date) {
    const startDate = new Date(data.start_date || existingCampaign.start_date)
    const endDate = new Date(data.end_date || existingCampaign.end_date)

    if (endDate < startDate) {
      throw new Error('End date must be after start date')
    }
  }

  // Validate advertiser if being changed
  if (data.advertiser_id) {
    const { data: advertiser, error: advertiserError } = await supabaseClient
      .from('advertisers')
      .select('id')
      .eq('id', data.advertiser_id)
      .single()

    if (advertiserError || !advertiser) {
      throw new Error('Advertiser not found')
    }
  }

  // Prepare status history update if status is changing
  let statusHistoryUpdate = existingCampaign.status_history || []
  if (data.status && data.status !== existingCampaign.status) {
    statusHistoryUpdate = [
      ...statusHistoryUpdate,
      {
        from: existingCampaign.status,
        to: data.status,
        timestamp: new Date().toISOString(),
        action: 'manual_update'
      }
    ]
  }

  // Update the campaign with audit trail
  const updateData = {
    ...data,
    updated_at: new Date().toISOString(),
    ...(data.status ? { status_history: statusHistoryUpdate } : {})
  }

  const { data: campaign, error } = await supabaseClient
    .from('campaigns')
    .update(updateData)
    .eq('id', id)
    .select(`
      *,
      advertisers (
        id,
        name,
        category,
        contact_email,
        logo_url
      )
    `)
    .single()

  if (error) {
    console.error('Error updating campaign:', error)
    throw new Error(error.message)
  }

  return campaign
}

export async function createCampaign(data: CreateCampaignData) {
  // First validate that the advertiser exists
  const { data: advertiser, error: advertiserError } = await supabaseClient
    .from('advertisers')
    .select('id')
    .eq('id', data.advertiser_id)
    .single()

  if (advertiserError || !advertiser) {
    throw new Error('Advertiser not found')
  }

  // Validate date range
  const startDate = new Date(data.start_date)
  const endDate = new Date(data.end_date)

  if (endDate < startDate) {
    throw new Error('End date must be after start date')
  }

  // Create the campaign
  const { data: campaign, error } = await supabaseClient
    .from('campaigns')
    .insert({
      name: data.name,
      advertiser_id: data.advertiser_id,
      tier: data.tier,
      slot_type: 'Events List',
      language_target: data.language_target,
      platform_target: data.platform_target,
      start_date: data.start_date,
      end_date: data.end_date,
      status: 'Draft'
    })
    .select(`
      *,
      advertisers (
        id,
        name,
        category,
        contact_email,
        logo_url
      )
    `)
    .single()

  if (error) {
    console.error('Error creating campaign:', error)
    throw new Error(error.message)
  }

  return campaign
}